/**
 * Where the widget's configuration comes from, and in what order.
 *
 * Until 1.6.0 everything came from the pasted snippet, frozen at paste time.
 * The console let an operator edit the greeting, press Save, read "Saved" —
 * and their live site kept the old greeting until someone re-pasted the
 * snippet (shared-chatbot review B2). Now the widget asks the server first:
 *
 *   GET {apiUrl}/chat/config?tenantId=   (anonymous, origin-guarded)
 *
 * and a value the server has SET wins over the snippet prop, which wins over
 * the localised default. "Set" means non-empty: '' and [] are how the API says
 * "not configured", and then the snippet still applies. Position and offset
 * are the exception — the console owns them outright, so when the server
 * sends them they win even at their defaults.
 *
 * When the server cannot answer (network, 5xx, the route not deployed yet)
 * the props apply as before, with one console.warn. When it answers with a
 * reason NOT to render — unknown tenant, this origin not allowed, assistant
 * unavailable — the widget renders nothing and says why in the console, so
 * the person who can fix it learns what to fix.
 */
import { parseHumanCheck, type HumanCheckConfig } from './humanCheck';
import { DEFAULT_LEAD_FORM, parseLeadForm, type LeadFormConfig } from './lead';
import { detectLanguage, stringsFor, type WidgetStrings } from './strings';
import { normalizeBrandColor } from './theme';
import type { ChatWidgetProps, PweChatConfig } from './types';

/** One to 64 characters: a letter or digit first, then letters, digits, '-' or '_'. */
export const TENANT_ID_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;

export type Position = 'bottom-right' | 'bottom-left';

/**
 * WHERE A VISITOR IS SENT WHEN THE BUSINESS NAMED NOWHERE.
 *
 * PuglieseWeb LTD's own notice. It is a FALLBACK, never a substitute: the
 * business running this widget is the controller of the conversation and we are
 * its processor, so its notice is the one that should be linked and `privacyUrl`
 * is how it says which. But `privacyUrl` is optional, and until now the whole
 * privacy line was `{privacyUrl && ...}` — a tenant who never filled the field
 * in shipped an assistant that stores what visitors type and tells them nothing
 * at all. A notice a tenant can switch off by leaving a box empty is not a
 * notice, and the omission is ours: we ship the widget.
 *
 * So the line always renders. Without a tenant URL it points here, under
 * `privacyOperatorLinkLabel` rather than "Privacy Policy", because this is not
 * the policy of the company whose website the visitor is on.
 */
export const OPERATOR_PRIVACY_URL = 'https://www.puglieseweb.com/privacy';

export interface Offset {
  bottom: number;
  side: number;
}

export function isAbsoluteHttpsUrl(value: unknown): value is string {
  if (typeof value !== 'string' || !value.trim()) return false;
  try {
    return new URL(value.trim()).protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * `avatarUrl` / `privacyUrl` land in `<img src>` and `<a href>`, and
 * `feedbackApiUrl` in a `fetch()` carrying what a visitor typed: only an
 * absolute https URL is accepted, from the server record and the snippet
 * alike (the backend validates too, but a `javascript:` or `data:` value from
 * a mis-validated tenant record must not reach the DOM). Anything else reads
 * as unset, with one console.warn naming the option.
 */
export function httpsUrlOrEmpty(value: unknown, option: string, origin: 'server' | 'snippet'): string {
  if (typeof value !== 'string' || value.trim() === '') return '';
  if (isAbsoluteHttpsUrl(value)) return value.trim();
  console.warn(`[PweChat] Ignoring ${origin} ${option} ${JSON.stringify(value)} — expected an absolute https:// URL.`);
  return '';
}

export function validPosition(value: unknown): Position | null {
  return value === 'bottom-right' || value === 'bottom-left' ? value : null;
}

/**
 * An offset object with both members as non-negative finite pixels, or null
 * when the input is not an object at all. Bad members become 0 — the embed
 * path has already warned about them in `validateConfig`; the React path
 * (an Astro island, the console preview) just never throws.
 */
export function coerceOffset(raw: unknown): Offset | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const px = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  return { bottom: px(o.bottom), side: px(o.side) };
}

// ── Snippet validation (embed path) ─────────────────────────────

const LEGACY_PROPS = ['getStartedUrl', 'getStartedLabel'];

/**
 * Validate the runtime-supplied options from a hand-authored <script>
 * snippet. Two kinds of failure:
 *
 *   - `apiUrl` / `tenantId` wrong or missing: the widget CANNOT work, and
 *     mounting it anyway used to look like "the assistant has no knowledge"
 *     (review S2) — a typo'd tenant id reached the backend, which answered
 *     with the no-content reply as if it were a normal answer. So this logs
 *     a console.error naming the fix and returns null: do not mount.
 *   - anything else (position, offset, colour): dropped with a console.warn
 *     so the component falls back to its default. A typo in one option never
 *     takes the whole widget down.
 *
 * Never throws.
 */
export function validateConfig(config: PweChatConfig): PweChatConfig | null {
  if (!config || typeof config !== 'object') {
    console.error(
      '[PweChat] init() needs an options object: PweChat.init({ apiUrl, tenantId, ... }). The widget was not mounted.',
    );
    return null;
  }
  const safe: PweChatConfig = { ...config };

  if (!isAbsoluteHttpsUrl(safe.apiUrl)) {
    console.error(
      `[PweChat] apiUrl must be an absolute https:// URL (got ${JSON.stringify(config.apiUrl)}). ` +
        'The widget was not mounted — copy the snippet again from the Ema Answers console (Setup → Install).',
    );
    return null;
  }
  safe.apiUrl = safe.apiUrl.trim().replace(/\/+$/, '');

  if (typeof safe.tenantId !== 'string' || !TENANT_ID_RE.test(safe.tenantId)) {
    console.error(
      `[PweChat] tenantId ${JSON.stringify(config.tenantId)} is missing or invalid — expected 1–64 letters, digits, '-' or '_'. ` +
        'The widget was not mounted — copy the snippet again from the Ema Answers console (Setup → Install).',
    );
    return null;
  }

  if (safe.position !== undefined && !validPosition(safe.position)) {
    console.warn(
      `[PweChat] Ignoring invalid position ${JSON.stringify(safe.position)} — ` +
        `expected 'bottom-right' or 'bottom-left'. Using the default.`,
    );
    delete safe.position;
  }

  if (safe.offset !== undefined) {
    const raw = safe.offset as unknown;
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      console.warn(
        `[PweChat] Ignoring invalid offset ${JSON.stringify(safe.offset)} — ` +
          `expected an object like { bottom?: number, side?: number } in pixels.`,
      );
      delete safe.offset;
    } else {
      const source = raw as Record<string, unknown>;
      const clean: { bottom?: number; side?: number } = {};
      (['bottom', 'side'] as const).forEach((key) => {
        const value = source[key];
        if (value === undefined) return;
        if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
          console.warn(
            `[PweChat] Ignoring invalid offset.${key} ${JSON.stringify(value)} — ` +
              `expected a non-negative number of pixels.`,
          );
        } else {
          clean[key] = value;
        }
      });
      safe.offset = clean;
    }
  }

  if (safe.brandColor !== undefined) {
    // Normalised here (with the warning) so resolveConfig sees a clean value
    // and does not warn a second time.
    safe.brandColor = normalizeBrandColor(safe.brandColor);
  }

  for (const key of LEGACY_PROPS) {
    if (Object.prototype.hasOwnProperty.call(config, key)) {
      console.warn(
        `[PweChat] ${key} is ignored since chat-widget 1.6.0 — lead capture is switched on in the Ema Answers console instead.`,
      );
    }
  }

  return safe;
}

// ── Server configuration (GET /chat/config) ─────────────────────

export interface ServerConfig {
  tenantId: string;
  name: string;
  welcomeMessage: string;
  suggestedReplies: string[];
  /** '' or a hex colour as the operator typed it (normalised in resolveConfig). */
  brandColor: string;
  avatarUrl: string;
  widgetPosition: Position | null;
  widgetOffset: Offset | null;
  assistantLanguage: string;
  matchVisitorLanguage: boolean;
  chatbotEnabled: boolean;
  leadCaptureEnabled: boolean;
  /**
   * Whether a "Book an appointment" chip may stand beside the conversation
   * (ADR-110 Am.7). Both halves of the backend's own decision: the stack
   * switch and the business's choice. Absent reads as false — a widget
   * talking to a backend from before this field must draw no booking chip.
   */
  bookingEnabled: boolean;
  /** What the "Leave your details" form asks (ADR-114 Am.2); today's form when absent. */
  leadForm: LeadFormConfig;
  privacyUrl: string;
  /**
   * Where the "Send feedback" form posts (`{feedbackApiUrl}/v1/feedback`), or
   * '' for no button. Stack-wide like `streamUrl`, but unlike it a prop too: a
   * surface that mounts with `fetchConfig: false` can still draw the button.
   */
  feedbackApiUrl: string;
  /** The link chip's words and address; the widget draws it only when both are set. */
  linkChipLabel: string;
  linkChipUrl: string;
  /** The client's own chat button as a CSS selector, or '' for the widget's launcher. */
  launcherSelector: string;
  /**
   * Where a turn is streamed from (ADR-103 §4): the base of the streaming
   * endpoint, or '' when the deployment has none. Server-only — never a
   * prop — because it is a property of the backend, not of the site.
   */
  streamUrl: string;
  /**
   * The human check to run before a turn (ADR-123), or null when the backend
   * has none. Server-only like `streamUrl`: a snippet cannot switch it off.
   */
  humanCheck: HumanCheckConfig | null;
}

export type BlockedCode = 'tenant_unknown' | 'origin_not_allowed' | 'assistant_unavailable';

export type ConfigOutcome =
  | { kind: 'ok'; config: ServerConfig }
  /** Could not get an answer: network, timeout, 5xx, route absent, malformed. Use the props. */
  | { kind: 'fallback'; reason: string }
  /** The server answered with a reason not to render at all. */
  | { kind: 'blocked'; code: BlockedCode };

/** Shape check for the JSON body; lenient about absent keys, strict about types. */
export function parseServerConfig(raw: unknown): ServerConfig | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const str = (key: string): string => (typeof o[key] === 'string' ? (o[key] as string).trim() : '');
  const bool = (key: string, fallback: boolean): boolean => (typeof o[key] === 'boolean' ? (o[key] as boolean) : fallback);
  const suggestedReplies = Array.isArray(o.suggestedReplies)
    ? (o.suggestedReplies as unknown[]).filter((s): s is string => typeof s === 'string' && s.trim() !== '').map((s) => s.trim())
    : [];
  return {
    tenantId: str('tenantId'),
    name: str('name'),
    welcomeMessage: str('welcomeMessage'),
    suggestedReplies,
    brandColor: str('brandColor'),
    avatarUrl: httpsUrlOrEmpty(o.avatarUrl, 'avatarUrl', 'server'),
    widgetPosition: validPosition(o.widgetPosition),
    widgetOffset: coerceOffset(o.widgetOffset),
    assistantLanguage: str('assistantLanguage'),
    matchVisitorLanguage: bool('matchVisitorLanguage', true),
    chatbotEnabled: bool('chatbotEnabled', true),
    leadCaptureEnabled: bool('leadCaptureEnabled', false),
    bookingEnabled: bool('bookingEnabled', false),
    leadForm: parseLeadForm(o.leadForm),
    privacyUrl: httpsUrlOrEmpty(o.privacyUrl, 'privacyUrl', 'server'),
    feedbackApiUrl: httpsUrlOrEmpty(o.feedbackApiUrl, 'feedbackApiUrl', 'server').replace(/\/+$/, ''),
    linkChipLabel: str('linkChipLabel'),
    linkChipUrl: httpsUrlOrEmpty(o.linkChipUrl, 'linkChipUrl', 'server'),
    launcherSelector: str('launcherSelector'),
    streamUrl: httpsUrlOrEmpty(o.streamUrl, 'streamUrl', 'server').replace(/\/+$/, ''),
    humanCheck: parseHumanCheck(o.humanCheck),
  };
}

const BLOCKED_CODES = new Set<string>(['tenant_unknown', 'origin_not_allowed', 'assistant_unavailable']);

export async function fetchServerConfig(
  apiUrl: string,
  tenantId: string,
  opts: { timeoutMs?: number; signal?: AbortSignal } = {},
): Promise<ConfigOutcome> {
  const { timeoutMs = 6_000, signal } = opts;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const onOuterAbort = () => ctrl.abort();
  if (signal) signal.addEventListener('abort', onOuterAbort);
  try {
    const res = await fetch(`${apiUrl}/chat/config?tenantId=${encodeURIComponent(tenantId)}`, {
      signal: ctrl.signal,
    });
    if (res.status === 403 || res.status === 404) {
      const body = await res.json().catch(() => null);
      const code = body && typeof body === 'object' && typeof body.code === 'string' ? body.code : '';
      if (BLOCKED_CODES.has(code)) return { kind: 'blocked', code: code as BlockedCode };
      // A 403/404 WITHOUT one of our codes is API Gateway's own answer
      // ("Missing Authentication Token") — the route is not deployed on this
      // API yet. That is not a reason to hide the widget.
      return { kind: 'fallback', reason: `HTTP ${res.status}` };
    }
    if (!res.ok) return { kind: 'fallback', reason: `HTTP ${res.status}` };
    const json = await res.json().catch(() => null);
    const parsed = parseServerConfig(json);
    if (!parsed) return { kind: 'fallback', reason: 'malformed response' };
    return { kind: 'ok', config: parsed };
  } catch {
    return { kind: 'fallback', reason: ctrl.signal.aborted ? 'timeout' : 'network error' };
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onOuterAbort);
  }
}

// ── Resolution: server → props → localised defaults ─────────────

export interface ResolvedConfig {
  /** Whether the server configuration was applied. */
  source: 'server' | 'props';
  /** BCP-47 tag the chrome is rendered in and replies are requested in. */
  language: string;
  strings: WidgetStrings;
  businessName: string;
  greeting: string;
  suggestions: string[];
  /** Normalised `#rrggbb`. */
  brandColor: string;
  avatarUrl?: string;
  position: Position;
  offset: Offset;
  privacyUrl?: string;
  /** The feedback service's base URL, no trailing slash; undefined = no "Send feedback" button. */
  feedbackApiUrl?: string;
  /** The page's own chat button; undefined = the widget draws its launcher. */
  launcherSelector?: string;
  /** The link chip, or null when no complete pair is configured. */
  linkChip: { label: string; url: string } | null;
  leadCaptureEnabled: boolean;
  /**
   * Whether the standing "Book an appointment" chip is drawn (ADR-110 Am.7).
   * From the server whenever it answered, like the switch above it.
   */
  bookingEnabled: boolean;
  /**
   * What the lead form asks. From the server whenever it answered, like the
   * switch above it, so a snippet cannot change the form a business set; the
   * `leadForm` prop applies only to a surface mounted without the config
   * (1.16.0), and without either the form is today's.
   */
  leadForm: LeadFormConfig;
  /** The pill beside the closed launcher; `null` = no pill. Snippet-only: the server config does not carry it. */
  launcherLabel: string | null;
  /** The streaming endpoint's base, from the server only; '' = send over the REST route. */
  streamUrl: string;
  /**
   * The human check, from the server only (ADR-123); null = none known yet. A
   * surface that mounts without the config learns it from the first refusal.
   */
  humanCheck: HumanCheckConfig | null;
}

function firstSet(...values: unknown[]): string | undefined {
  for (const v of values) {
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
  }
  return undefined;
}

function nonEmptyList(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const clean = value.filter((s): s is string => typeof s === 'string' && s.trim() !== '').map((s) => s.trim());
  return clean.length ? clean : undefined;
}

/**
 * The link chip is a PAIR: a label without an address, or an address without a
 * label, draws nothing — and the two halves never come from different places,
 * because a server label on a snippet's URL would be a chip nobody configured.
 */
function linkChipFrom(props: ChatWidgetProps, server: ServerConfig | null): { label: string; url: string } | null {
  if (server?.linkChipLabel && server.linkChipUrl) return { label: server.linkChipLabel, url: server.linkChipUrl };
  const label = firstSet(props.linkChipLabel);
  const url = httpsUrlOrEmpty(props.linkChipUrl, 'linkChipUrl', 'snippet');
  return label && url ? { label, url } : null;
}

export function resolveConfig(props: ChatWidgetProps, server: ServerConfig | null): ResolvedConfig {
  const requested = firstSet(props.language) ?? detectLanguage();
  // The operator turned "match the visitor's language" off and chose the
  // assistant's language: the chrome follows the assistant, so the greeting,
  // the chips and the reply all read in one language.
  const language = server && !server.matchVisitorLanguage && server.assistantLanguage ? server.assistantLanguage : requested;
  const strings = stringsFor(language);

  return {
    source: server ? 'server' : 'props',
    language,
    strings,
    businessName: firstSet(server?.name, props.businessName) ?? 'AI Assistant',
    greeting: firstSet(server?.welcomeMessage, props.greeting) ?? strings.greeting,
    suggestions: nonEmptyList(server?.suggestedReplies) ?? nonEmptyList(props.defaultSuggestions) ?? strings.suggestions,
    brandColor: normalizeBrandColor(firstSet(server?.brandColor, props.brandColor)),
    avatarUrl: firstSet(server?.avatarUrl, httpsUrlOrEmpty(props.avatarUrl, 'avatarUrl', 'snippet')),
    position: (server && server.widgetPosition) ?? validPosition(props.position) ?? 'bottom-right',
    offset: (server && server.widgetOffset) ?? coerceOffset(props.offset) ?? { bottom: 0, side: 0 },
    privacyUrl: firstSet(server?.privacyUrl, httpsUrlOrEmpty(props.privacyUrl, 'privacyUrl', 'snippet')),
    feedbackApiUrl: firstSet(server?.feedbackApiUrl, httpsUrlOrEmpty(props.feedbackApiUrl, 'feedbackApiUrl', 'snippet'))?.replace(/\/+$/, ''),
    launcherSelector: firstSet(server?.launcherSelector, props.launcherSelector),
    linkChip: linkChipFrom(props, server),
    leadCaptureEnabled: server ? server.leadCaptureEnabled : props.leadCaptureEnabled === true,
    bookingEnabled: server ? server.bookingEnabled : props.bookingEnabled === true,
    leadForm: server ? server.leadForm : props.leadForm ? parseLeadForm(props.leadForm) : DEFAULT_LEAD_FORM,
    launcherLabel: props.launcherLabel === false ? null : (firstSet(props.launcherLabel) ?? strings.talkToSite),
    streamUrl: server?.streamUrl ?? '',
    humanCheck: server?.humanCheck ?? null,
  };
}
